import { CheckCircle2, XCircle, Mail } from "lucide-react";
import { Switch } from "../ui/switch";
import { inputCls } from "./adminConstants";

export function EmailView({ ctx }) {
  const {
    emailCfg, setEmailCfg, emailSecret, setEmailSecret,
    saveEmailConfig, savingEmail, emailTestTo, setEmailTestTo,
    sendEmailTest, sendingTest,
  } = ctx;

  const cfg = emailCfg || {};
  const isConfigured = !!cfg.configured;
  const isEnabled = !!cfg.enabled;

  return (
    <div className="p-6 lg:p-12 max-w-2xl" data-testid="email-view">
      <h2 className="font-serif-display text-3xl text-[#E8E2D2] mb-2">Email — Microsoft 365</h2>
      <p className="font-mono-accent text-[10px] uppercase tracking-widest text-[#E8E2D2]/40 mb-6">
        Collega la tua casella Microsoft 365 per inviare conferme d'ordine reali tramite Microsoft Graph (OAuth2). Le credenziali sono cifrate nel database.
      </p>

      <div className="flex items-center gap-3 mb-8" data-testid="email-status">
        {isConfigured ? (
          <span className={`inline-flex items-center gap-2 font-mono-accent text-[10px] uppercase tracking-widest px-3 py-2 border ${isEnabled ? "bg-[#4A6E4A]/20 text-[#4A6E4A] border-[#4A6E4A]/40" : "bg-[#D47738]/15 text-[#D47738] border-[#D47738]/40"}`}>
            {isEnabled ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Mail className="w-3.5 h-3.5" />}
            {isEnabled ? "Invio reale ATTIVO" : "Configurato — invio reale disattivo"}
          </span>
        ) : (
          <span className="inline-flex items-center gap-2 font-mono-accent text-[10px] uppercase tracking-widest px-3 py-2 border bg-[#9E3C3C]/20 text-[#9E3C3C] border-[#9E3C3C]/40">
            <XCircle className="w-3.5 h-3.5" /> Non configurato
          </span>
        )}
      </div>

      <div className="border border-white/15 bg-[#141A14] p-6 mb-6 text-sm text-[#E8E2D2]/60 leading-relaxed" data-testid="email-guide">
        <p className="font-mono-accent text-[10px] uppercase tracking-widest text-[#D47738] mb-3">Come ottenere le credenziali</p>
        <ol className="list-decimal pl-5 space-y-1">
          <li>Vai su <span className="text-[#E8E2D2]">entra.microsoft.com</span> → App registrations → New registration.</li>
          <li>Copia <b>Directory (tenant) ID</b> e <b>Application (client) ID</b> dalla Overview.</li>
          <li>API permissions → Microsoft Graph → Application permissions → <b>Mail.Send</b> → concedi il consenso admin.</li>
          <li>Certificates &amp; secrets → New client secret → copia il <b>Value</b>.</li>
          <li>Indica come mittente una casella Microsoft 365 con licenza (es. ordini@tuodominio.it).</li>
        </ol>
      </div>

      <div className="space-y-4 mb-8">
        <div>
          <label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#E8E2D2]/50">Tenant ID (Directory)</label>
          <input value={cfg.tenant_id || ""} onChange={(e) => setEmailCfg({ ...cfg, tenant_id: e.target.value })}
            placeholder="es. 00000000-0000-0000-0000-000000000000"
            className={inputCls + " mt-1"} data-testid="email-tenant-id" />
        </div>
        <div>
          <label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#E8E2D2]/50">Client ID (Application)</label>
          <input value={cfg.client_id || ""} onChange={(e) => setEmailCfg({ ...cfg, client_id: e.target.value })}
            placeholder="es. 11111111-1111-1111-1111-111111111111"
            className={inputCls + " mt-1"} data-testid="email-client-id" />
        </div>
        <div>
          <label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#E8E2D2]/50">
            Client Secret {cfg.has_secret ? "(salvato — lascia vuoto per non modificarlo)" : ""}
          </label>
          <input type="password" value={emailSecret} onChange={(e) => setEmailSecret(e.target.value)}
            placeholder={cfg.has_secret ? "••••••••••••" : "Incolla qui il Value del secret"}
            className={inputCls + " mt-1"} data-testid="email-client-secret" />
        </div>
        <div>
          <label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#E8E2D2]/50">Email mittente (casella M365)</label>
          <input type="email" value={cfg.sender_email || ""} onChange={(e) => setEmailCfg({ ...cfg, sender_email: e.target.value })}
            placeholder="es. ordini@tuodominio.it"
            className={inputCls + " mt-1"} data-testid="email-sender" />
        </div>
      </div>

      <div className="flex items-center justify-between border border-white/15 bg-[#141A14] px-6 py-5 mb-8" data-testid="email-enabled-bar">
        <div>
          <p className="font-serif-display text-xl text-[#E8E2D2]">Attiva invio reale via Microsoft 365</p>
          <p className="font-mono-accent text-[10px] uppercase tracking-widest text-[#E8E2D2]/40 mt-1">
            Se disattivo, le email usano il canale di test predefinito
          </p>
        </div>
        <Switch checked={!!cfg.enabled} onCheckedChange={(v) => setEmailCfg({ ...cfg, enabled: v })}
          className="data-[state=checked]:bg-[#D47738]" data-testid="email-enabled-switch" />
      </div>

      <button onClick={saveEmailConfig} disabled={savingEmail}
        className="bg-[#D47738] text-[#0D110D] px-8 py-4 font-mono-accent text-xs uppercase tracking-[0.2em] hover:bg-[#E8E2D2] transition-colors disabled:opacity-50"
        data-testid="save-email-btn">
        {savingEmail ? "Salvataggio..." : "Salva configurazione email"}
      </button>

      <div className="border-t border-white/10 pt-10 mt-12" data-testid="email-test-section">
        <h3 className="font-serif-display text-2xl text-[#E8E2D2] mb-2">Invia email di prova</h3>
        <p className="font-mono-accent text-[10px] uppercase tracking-widest text-[#E8E2D2]/40 mb-6">
          Salva prima la configurazione. La prova viene inviata realmente tramite Microsoft 365.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <input type="email" value={emailTestTo} onChange={(e) => setEmailTestTo(e.target.value)}
            placeholder="Destinatario (vuoto = email amministratore)"
            className={inputCls + " flex-1"} data-testid="email-test-to" />
          <button onClick={sendEmailTest} disabled={sendingTest || !isConfigured}
            className="border border-white/20 px-6 py-3 font-mono-accent text-xs uppercase tracking-[0.2em] text-[#E8E2D2] hover:border-[#D47738] hover:text-[#D47738] transition-colors disabled:opacity-40"
            data-testid="send-test-email-btn">
            {sendingTest ? "Invio..." : "Invia prova"}
          </button>
        </div>
      </div>
    </div>
  );
}
